import type { IndustryContent } from '@/types/content';

export const windowCleaningContent: IndustryContent = {
  slug: 'window-cleaning',
  name: 'Window Cleaning',
  headline: 'The Operating System for Window Cleaning Companies',
  subheadline:
    'Route residential rounds efficiently, manage commercial contracts, and scale your crews—all from one platform built for window cleaners.',
  heroImageUrl: '/images/industries/window-cleaning-hero.webp',
  painPoints: [
    {
      title: 'Route rounds take forever to build',
      description:
        'Manually building efficient routes through neighborhoods is tedious. One wrong address throws off the whole day.',
      icon: 'route',
    },
    {
      title: 'Recurring schedules get messy',
      description:
        'Monthly residential, quarterly commercial, bi-annual high-rise—tracking different frequencies across hundreds of customers is overwhelming.',
      icon: 'calendar-x',
    },
    {
      title: 'Weather ruins your week',
      description:
        'Rain cancels Tuesday, but you still have Wednesday, Thursday, and Friday packed. Reshuffling is a manual nightmare.',
      icon: 'weather',
    },
    {
      title: 'Commercial billing is complex',
      description:
        'Per-pane pricing, monthly contracts, add-on services—invoicing commercial accounts accurately takes too long.',
      icon: 'invoice',
    },
    {
      title: 'Quality issues cost you customers',
      description:
        'Missed panes, streaks, or skipped windows—without a system to verify work, complaints pile up.',
      icon: 'quality',
    },
  ],
  features: [
    {
      title: 'Route Round Building',
      description:
        'Build tight geographic routes that keep crews in neighborhoods. Optimize for drive time and logical flow.',
      icon: 'route',
    },
    {
      title: 'Multi-Frequency Scheduling',
      description:
        'Handle weekly, monthly, quarterly, and custom frequencies. The system knows who needs service when.',
      icon: 'recurring',
    },
    {
      title: 'Weather Rescheduling',
      description:
        'One-click reschedule for weather days. Automatically notify affected customers and rebuild routes.',
      icon: 'weather',
    },
    {
      title: 'Commercial Contract Management',
      description:
        'Track contracts, pricing tiers, and billing cycles. Auto-generate invoices based on completed work.',
      icon: 'contract',
    },
    {
      title: 'Window Count & Pricing',
      description:
        'Store pane counts per property. Quote accurately and track profitability by job.',
      icon: 'pricing',
    },
    {
      title: 'Quality Verification',
      description:
        'Photo documentation and digital checklists ensure every window is done right. Reduce callbacks.',
      icon: 'quality',
    },
  ],
  testimonial: {
    id: 'window-cleaning-1',
    quote:
      'We used to spend Sunday nights building routes for the week. Now it takes 10 minutes. Our crews are hitting 20% more stops per day.',
    author: 'David Chen',
    role: 'Owner',
    company: 'Crystal Clear Windows',
    industry: 'window-cleaning',
    stats: {
      label: 'More Stops Per Day',
      value: '+20%',
    },
  },
  stats: [
    { label: 'Route Planning Time', value: '-90%' },
    { label: 'More Stops Per Day', value: '+20%' },
    { label: 'Billing Accuracy', value: '99%' },
    { label: 'Customer Retention', value: '94%' },
  ],
  seo: {
    title: 'Window Cleaning Software | Routing & Scheduling | Omniforge',
    description:
      'Window cleaning software for residential and commercial cleaners. Route optimization, recurring scheduling, and commercial billing. Start your free trial.',
    keywords: [
      'window cleaning software',
      'window washer software',
      'window cleaning scheduling',
      'window cleaning business software',
      'window cleaning route software',
    ],
  },
};
